import React, { useEffect, useState } from 'react';
import { RiAlertLine } from '@remixicon/react';

import { Button } from '@/design-system/button';
import { Field } from '@/design-system/field';
import { FormModal } from '@/design-system/form-modal';
import { Label } from '@/design-system/label';
import { Textarea } from '@/design-system/textarea';
import { toast } from '@/design-system/toaster';
import { Shipment, shipmentsAPI } from '@/services/api/shipments';

interface CancelShipmentModalProps {
  /** The shipment to cancel; when null the modal is closed. */
  shipment: Pick<Shipment, 'id' | 'waybill' | 'bookingId'> | null;
  onClose: () => void;
  /** Called with the cancelled shipment the server returns. */
  onCancelled: (updated: Shipment) => void;
}

/**
 * Cancel a shipment while it is still CREATED (`POST /v1/shipments/:id/cancel`):
 * every non-terminal item is cancelled too, and a DIRECT_4W route with it.
 * Shared by the shipments-list row kebab and the shipment detail, where the
 * Cancel stop modal offers it for the last open stop of an unstarted route
 * (E27, eng review D3).
 */
const CancelShipmentModal: React.FC<CancelShipmentModalProps> = ({
  shipment,
  onClose,
  onCancelled,
}) => {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // A fresh form per shipment.
  useEffect(() => {
    setReason('');
  }, [shipment?.id]);

  const close = () => {
    if (submitting) return;
    setReason('');
    onClose();
  };

  const submit = async () => {
    if (!shipment) return;
    setSubmitting(true);
    try {
      const updated = await shipmentsAPI.cancel(shipment.id, {
        note: reason.trim() || undefined,
      });
      toast.success('Shipment cancelled', {
        description: `${
          updated.waybill || updated.bookingId
        } marked as cancelled.`,
      });
      setReason('');
      onCancelled(updated);
    } catch (err) {
      toast.error('Could not cancel shipment', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormModal
      open={shipment !== null}
      onClose={close}
      icon={<RiAlertLine />}
      iconTone="danger"
      title="Cancel this shipment?"
      subtitle={
        shipment ? `${shipment.waybill} · ${shipment.bookingId}` : undefined
      }
      size="sm"
      closeOnBackdropClick={!submitting}
      closeOnEscape={!submitting}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button
            tone="neutral"
            style="stroke"
            onClick={close}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            tone="destructive"
            onClick={submit}
            disabled={submitting}
            loading={submitting}
          >
            {submitting ? 'Cancelling…' : 'Cancel shipment'}
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-text-sub-600">
          All non-terminal items will be cancelled too. This only works while
          the shipment is still <strong>CREATED</strong> (pre-dispatch).
        </p>
        <Field>
          <Label htmlFor="cancel-reason">Reason (optional)</Label>
          <Textarea
            id="cancel-reason"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why is this shipment being cancelled?"
            disabled={submitting}
          />
        </Field>
      </div>
    </FormModal>
  );
};

export default CancelShipmentModal;
